import React, { useState, useEffect, useRef, useMemo } from 'react';
import './InstructorStudents.css';

const ICON_BOOK = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const getCourseLabel = (c) => {
  if (!c) return '';
  const code = (c.course_code || '').trim().toUpperCase();
  const name = c.course_name || `Ders ${c.course_id}`;
  return code ? `${code} — ${name}` : name;
};

function CourseDropdown({
  courses = [],
  selectedCourseId,
  onChange,
  loading = false,
  placeholder = 'Ders seçiniz',
  searchPlaceholder = 'Ders kodu veya adı ara...',
  showBadges = true,
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLocaleLowerCase('tr');
    if (!q) return courses;
    return courses.filter((c) =>
      (c.course_code || '').toLocaleLowerCase('tr').includes(q) ||
      (c.course_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.program_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.faculty_name || '').toLocaleLowerCase('tr').includes(q)
    );
  }, [courses, search]);

  const selected = courses.find((c) => String(c.course_id) === String(selectedCourseId));
  const label = selected ? getCourseLabel(selected) : (loading ? 'Dersler yükleniyor...' : placeholder);

  return (
    <div className="is-course-filter" ref={rootRef} style={{ maxWidth: 'none', width: '100%', minWidth: 0 }}>
      <button
        type="button"
        className={`is-course-btn ${open ? 'open' : ''}`}
        onClick={() => { setOpen((o) => !o); setSearch(''); }}
      >
        <span className="is-course-btn-icon">{ICON_BOOK}</span>
        <span className="is-course-btn-label">{label}</span>
        {selectedCourseId ? (
          <span
            onClick={(e) => { e.stopPropagation(); onChange(''); setSearch(''); }}
            className="is-course-btn-chevron"
            style={{ cursor: 'pointer' }}
            title="Seçimi kaldır"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        ) : (
          <span className="is-course-btn-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </button>
      {open && (
        <div className="is-course-panel">
          <div className="is-course-search-wrap">
            <span className="is-course-search-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              autoFocus
              type="text"
              className="is-course-search-input"
              placeholder={loading ? 'Dersler yükleniyor...' : searchPlaceholder}
              disabled={loading}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="is-course-list">
            {filtered.length === 0 ? (
              <li className="is-course-empty">Sonuç bulunamadı</li>
            ) : (
              filtered.map((c) => {
                const isSel = String(selectedCourseId) === String(c.course_id);
                return (
                  <li
                    key={c.course_id}
                    className={`is-course-item ${isSel ? 'selected' : ''}`}
                    onClick={() => { onChange(c.course_id); setOpen(false); setSearch(''); }}
                  >
                    <span className="is-course-item-label">{getCourseLabel(c)}</span>
                    {showBadges && (c.faculty_name || c.program_name) && (
                      <span className="is-course-item-badges">
                        {c.faculty_name && (
                          <span className="is-course-item-faculty">{c.faculty_name}</span>
                        )}
                        {c.program_name && (
                          <span className="is-course-item-program">{c.program_name}</span>
                        )}
                      </span>
                    )}
                    {isSel && (
                      <span className="is-course-item-check">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CourseDropdown;
